import React from "react"

const T = {
  title: "Fusi\u00f3n de pacientes",
  badgeSelect: "Seleccionar destino",
  badgePreview: "Vista previa",
  badgeSuccess: "Completado",
  sourceLabel: "Paciente origen",
  searchPlaceholder: "Buscar paciente por nombre, tel\u00e9fono o correo...",
  targetLabel: "Paciente destino",
  previewTitle: "Movimientos que se mover\u00e1n",
  warningTitle: "Revisi\u00f3n requerida",
  noCandidates: "No encontr\u00e9 pacientes disponibles con esa b\u00fasqueda.",
  selectHelp: "Elige el paciente correcto para recibir citas, pagos, planes, odontograma e historia cl\u00ednica.",
  previewHelp: "Revisa el resumen antes de confirmar. El paciente origen ser\u00e1 eliminado al finalizar si queda sin movimientos ligados.",
  cancel: "Cancelar",
  back: "Volver",
  review: "Revisar fusi\u00f3n",
  confirm: "Confirmar fusi\u00f3n",
  merging: "Fusionando...",
  close: "Cerrar",
  successDefault: "Pacientes fusionados correctamente.",
  blocked: "Esta fusi\u00f3n no puede continuar autom\u00e1ticamente.",
  emptyCounts: "No hay movimientos ligados para mostrar."
}

const relationLabels = {
  appointments: "Citas",
  odontogramSurfaces: "Superficies de odontograma",
  odontogramEntries: "Odontograma",
  medicalHistories: "Historia m\u00e9dica",
  treatmentLogs: "Tratamientos realizados",
  payments: "Pagos",
  accountReceivables: "Cuentas por cobrar",
  treatmentPlans: "Planes de tratamiento",
  financialAuditEvents: "Eventos financieros",
  clinicalHistories: "Historias cl\u00ednicas",
  clinicalSpecialtyCases: "Casos cl\u00ednicos",
  clinicalSpecialtyEvolutions: "Evoluciones cl\u00ednicas",
  clinicalForms: "Formularios cl\u00ednicos"
}

function getPatientName(patient) {
  const fullName = `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim()

  return patient?.name || fullName || "Paciente sin nombre"
}

function getRows(preview) {
  const source =
    preview?.blockingCounts && Object.keys(preview.blockingCounts).length
      ? preview.blockingCounts
      : preview?.counts || {}

  return Object.entries(source)
    .filter(([, value]) => Number(value || 0) > 0)
    .map(([key, value]) => ({
      key,
      label: relationLabels[key] || key,
      value: Number(value || 0)
    }))
}

export default function PatientMergeModal({
  open,
  mode = "select",
  sourcePatientName = "este paciente",
  query = "",
  candidates = [],
  selectedTargetId = "",
  preview = null,
  loadingPreview = false,
  merging = false,
  error = "",
  resultMessage = "",
  onQueryChange,
  onSelectTarget,
  onPreview,
  onConfirmMerge,
  onBack,
  onClose
}) {
  if (!open) return null

  const rows = getRows(preview)
  const selectedTarget = candidates.find((patient) => patient?.id === selectedTargetId)
  const isPreview = mode === "preview"
  const isSuccess = mode === "success"
  const isSelect = mode === "select"
  const canConfirm = isPreview && preview?.canMerge && !merging

  const badgeText = isSuccess
    ? T.badgeSuccess
    : isPreview
      ? T.badgePreview
      : T.badgeSelect

  return (
    <div style={styles.backdrop} role="dialog" aria-modal="true">
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.iconWrap}>MERGE</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.badge}>{badgeText}</div>
            <h2 style={styles.title}>{T.title}</h2>
            <p style={styles.subtitle}>
              {isSuccess
                ? resultMessage || T.successDefault
                : isPreview
                  ? T.previewHelp
                  : T.selectHelp}
            </p>
          </div>

          <button type="button" onClick={onClose} style={styles.closeButton}>
            x
          </button>
        </div>

        <div style={styles.sourceBox}>
          <span>{T.sourceLabel}</span>
          <strong>{sourcePatientName}</strong>
        </div>

        {error ? <div style={styles.errorBox}>{error}</div> : null}

        {isSelect ? (
          <div style={styles.content}>
            <label style={styles.label}>{T.targetLabel}</label>

            <input
              value={query}
              onChange={(event) => onQueryChange?.(event.target.value)}
              placeholder={T.searchPlaceholder}
              style={styles.input}
            />

            <div style={styles.candidateList}>
              {candidates.length ? (
                candidates.map((patient) => {
                  const active = patient?.id === selectedTargetId

                  return (
                    <button
                      type="button"
                      key={patient.id}
                      onClick={() => onSelectTarget?.(patient.id)}
                      style={{
                        ...styles.candidateButton,
                        ...(active ? styles.candidateButtonActive : {})
                      }}
                    >
                      <span style={styles.candidateMain}>
                        <strong>{getPatientName(patient)}</strong>
                        <small>
                          {[patient?.phone, patient?.email].filter(Boolean).join(" · ") ||
                            "Sin contacto registrado"}
                        </small>
                      </span>

                      <span style={styles.radioDot}>{active ? "OK" : ""}</span>
                    </button>
                  )
                })
              ) : (
                <div style={styles.emptyBox}>{T.noCandidates}</div>
              )}
            </div>
          </div>
        ) : null}

        {isPreview ? (
          <div style={styles.content}>
            <div style={styles.targetBox}>
              <span>{T.targetLabel}</span>
              <strong>
                {preview?.targetPatient
                  ? getPatientName(preview.targetPatient)
                  : selectedTarget
                    ? getPatientName(selectedTarget)
                    : "Paciente destino"}
              </strong>
            </div>

            {preview?.warnings?.length ? (
              <div style={styles.warningBox}>
                <strong>{T.warningTitle}</strong>
                {preview.warnings.map((warning) => (
                  <span key={warning.code || warning.message}>
                    {warning.message || T.blocked}
                  </span>
                ))}
              </div>
            ) : null}

            <div style={styles.sectionTitle}>{T.previewTitle}</div>

            <div style={styles.relationGrid}>
              {rows.length ? (
                rows.map((row) => (
                  <div key={row.key} style={styles.relationItem}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))
              ) : (
                <div style={styles.emptyBox}>{T.emptyCounts}</div>
              )}
            </div>
          </div>
        ) : null}

        {isSuccess ? (
          <div style={styles.content}>
            <div style={styles.successBox}>
              <strong>{T.badgeSuccess}</strong>
              <span>{resultMessage || T.successDefault}</span>
            </div>
          </div>
        ) : null}

        <div style={styles.footer}>
          {isSelect ? (
            <>
              <button type="button" onClick={onClose} style={styles.secondaryButton}>
                {T.cancel}
              </button>

              <button
                type="button"
                onClick={onPreview}
                disabled={!selectedTargetId || loadingPreview}
                style={{
                  ...styles.primaryButton,
                  opacity: !selectedTargetId || loadingPreview ? 0.55 : 1,
                  cursor: !selectedTargetId || loadingPreview ? "not-allowed" : "pointer"
                }}
              >
                {loadingPreview ? "Revisando..." : T.review}
              </button>
            </>
          ) : null}

          {isPreview ? (
            <>
              <button type="button" onClick={onBack} disabled={merging} style={styles.secondaryButton}>
                {T.back}
              </button>

              <button
                type="button"
                onClick={onConfirmMerge}
                disabled={!canConfirm}
                style={{
                  ...styles.primaryButton,
                  opacity: canConfirm ? 1 : 0.55,
                  cursor: canConfirm ? "pointer" : "not-allowed"
                }}
              >
                {merging ? T.merging : T.confirm}
              </button>
            </>
          ) : null}

          {isSuccess ? (
            <button type="button" onClick={onClose} style={styles.primaryButton}>
              {T.close}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    background: "rgba(15, 23, 42, 0.52)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18
  },
  modal: {
    width: "min(720px, 100%)",
    maxHeight: "min(820px, calc(100vh - 36px))",
    overflow: "auto",
    background: "#ffffff",
    border: "1px solid rgba(226, 232, 240, 0.95)",
    borderRadius: 28,
    boxShadow: "0 28px 90px rgba(15, 23, 42, 0.28)",
    padding: 20
  },
  header: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start"
  },
  iconWrap: {
    width: 58,
    height: 48,
    borderRadius: 18,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 0.4,
    flexShrink: 0
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 10.5,
    fontWeight: 900,
    letterSpacing: 0.45,
    textTransform: "uppercase",
    marginBottom: 8,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8"
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 24,
    lineHeight: 1.08,
    letterSpacing: "-0.035em",
    fontWeight: 950
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#64748b",
    cursor: "pointer",
    fontSize: 14,
    lineHeight: 1,
    fontWeight: 900,
    flexShrink: 0
  },
  sourceBox: {
    marginTop: 16,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 4,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800
  },
  targetBox: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 4,
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 800
  },
  content: {
    marginTop: 16,
    display: "grid",
    gap: 12
  },
  label: {
    color: "#334155",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.45
  },
  input: {
    width: "100%",
    minHeight: 42,
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    padding: "0 13px",
    outline: "none",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    boxSizing: "border-box"
  },
  candidateList: {
    display: "grid",
    gap: 9,
    maxHeight: 280,
    overflow: "auto"
  },
  candidateButton: {
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    borderRadius: 16,
    padding: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    textAlign: "left",
    cursor: "pointer"
  },
  candidateButtonActive: {
    border: "1px solid #93c5fd",
    background: "#eff6ff",
    boxShadow: "0 12px 24px rgba(37, 99, 235, 0.10)"
  },
  candidateMain: {
    display: "grid",
    gap: 4,
    color: "#0f172a",
    fontSize: 14
  },
  radioDot: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 950,
    flexShrink: 0
  },
  sectionTitle: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.45
  },
  relationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10
  },
  relationItem: {
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    borderRadius: 16,
    padding: "11px 12px",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    color: "#475569",
    fontSize: 13,
    fontWeight: 800
  },
  warningBox: {
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#7c2d12",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 5,
    fontSize: 13,
    lineHeight: 1.45
  },
  errorBox: {
    marginTop: 14,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    borderRadius: 18,
    padding: 14,
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 800
  },
  successBox: {
    border: "1px solid #bbf7d0",
    background: "#f0fdf4",
    color: "#166534",
    borderRadius: 18,
    padding: 14,
    display: "grid",
    gap: 5,
    fontSize: 13,
    lineHeight: 1.45
  },
  emptyBox: {
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    borderRadius: 16,
    padding: 13,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.45
  },
  footer: {
    marginTop: 18,
    paddingTop: 14,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap"
  },
  secondaryButton: {
    minHeight: 40,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 900,
    padding: "0 15px",
    cursor: "pointer"
  },
  primaryButton: {
    minHeight: 40,
    borderRadius: 14,
    border: "1px solid #2563eb",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#ffffff",
    fontWeight: 950,
    padding: "0 15px",
    cursor: "pointer"
  }
}
